/**
 * Equipment Renderer - Draws HVAC equipment on canvas
 */

import { EQUIPMENT_TYPES } from '../../constants/hvacEquipment';

/**
 * Draw a single piece of HVAC equipment
 * Canvas context should already have scale transform applied
 * All coordinates are in world space (inches)
 */
export function drawEquipment(ctx, equipment, scale, isSelected = false, isPreview = false) {
  const equipType = EQUIPMENT_TYPES[equipment.type] || EQUIPMENT_TYPES['furnace-gas'];
  const width = equipType.width;
  const height = equipType.height;

  ctx.save();

  // Move to equipment position and apply rotation
  ctx.translate(equipment.x, equipment.y);
  ctx.rotate((equipment.rotation || 0) * Math.PI / 180);

  // Set styles
  const fillOpacity = isPreview ? 0.3 : 0.5;
  ctx.fillStyle = isSelected
    ? `rgba(0, 200, 255, ${fillOpacity})`
    : `${equipType.color}${Math.round(fillOpacity * 255).toString(16).padStart(2, '0')}`;
  ctx.strokeStyle = isSelected ? '#00c8ff' : equipType.strokeColor;
  ctx.lineWidth = (isSelected ? 2 : 1.5) / scale;

  // Draw equipment centered at origin
  const halfW = width / 2;
  const halfH = height / 2;

  // Main rectangle with rounded corners
  roundRect(ctx, -halfW, -halfH, width, height, 2 / scale);
  ctx.fill();
  ctx.stroke();

  // Draw symbol based on equipment type
  drawEquipmentSymbol(ctx, equipment.type, equipType, width, height, scale);

  // Draw label
  if (!isPreview) {
    drawEquipmentLabel(ctx, equipment, equipType, width, height, scale);
  }

  // Draw selection handles if selected
  if (isSelected) {
    drawEquipmentHandles(ctx, width, height, scale);
  }

  ctx.restore();
}

/**
 * Draw rounded rectangle
 */
function roundRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * Draw equipment-specific symbol
 */
function drawEquipmentSymbol(ctx, type, equipType, width, height, scale) {
  const halfW = width / 2;
  const halfH = height / 2;
  const color = equipType.strokeColor;
  const symbol = equipType.symbol;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1 / scale;

  switch (symbol) {
    case 'flame':
      // Gas furnace - flame icon
      drawFlameSymbol(ctx, halfW, halfH, scale);
      break;

    case 'bolt':
      // Electric furnace - lightning bolt
      drawBoltSymbol(ctx, halfW, halfH, scale);
      break;

    case 'fan':
      // AC condenser - fan
      drawFanSymbol(ctx, halfW, halfH, scale);
      break;

    case 'coil':
      // Air handler - coil pattern
      drawCoilSymbol(ctx, halfW, halfH, scale);
      break;

    case 'hp':
      // Heat pump - HP with arrows
      drawHeatPumpSymbol(ctx, halfW, halfH, scale);
      break;

    case 'head':
      // Mini split head - airflow lines
      drawMiniSplitHeadSymbol(ctx, halfW, halfH, scale);
      break;

    case 'ms':
      // Mini split outdoor
      drawMiniSplitOutdoorSymbol(ctx, halfW, halfH, scale);
      break;

    case 't':
      // Thermostat - circle with temp
      drawThermostatSymbol(ctx, halfW, halfH, scale);
      break;
  }

  ctx.restore();
}

function drawFlameSymbol(ctx, halfW, halfH, scale) {
  const size = Math.min(halfW, halfH) * 0.6;
  ctx.beginPath();
  ctx.moveTo(0, -size);
  ctx.bezierCurveTo(size * 0.5, -size * 0.3, size * 0.8, size * 0.3, 0, size);
  ctx.bezierCurveTo(-size * 0.8, size * 0.3, -size * 0.5, -size * 0.3, 0, -size);
  ctx.fill();

  // Inner flame
  ctx.fillStyle = 'rgba(255, 200, 100, 0.8)';
  const innerSize = size * 0.4;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.bezierCurveTo(innerSize * 0.4, innerSize * 0.3, innerSize * 0.5, innerSize * 0.6, 0, innerSize);
  ctx.bezierCurveTo(-innerSize * 0.5, innerSize * 0.6, -innerSize * 0.4, innerSize * 0.3, 0, 0);
  ctx.fill();
}

function drawBoltSymbol(ctx, halfW, halfH, scale) {
  const size = Math.min(halfW, halfH) * 0.7;
  ctx.beginPath();
  ctx.moveTo(size * 0.2, -size);
  ctx.lineTo(-size * 0.3, 0);
  ctx.lineTo(0, 0);
  ctx.lineTo(-size * 0.2, size);
  ctx.lineTo(size * 0.3, 0);
  ctx.lineTo(0, 0);
  ctx.closePath();
  ctx.fill();
}

function drawFanSymbol(ctx, halfW, halfH, scale) {
  const radius = Math.min(halfW, halfH) * 0.7;

  // Outer circle
  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, Math.PI * 2);
  ctx.stroke();

  // Center hub
  ctx.beginPath();
  ctx.arc(0, 0, radius * 0.15, 0, Math.PI * 2);
  ctx.fill();

  // Fan blades
  for (let i = 0; i < 4; i++) {
    const angle = (i * Math.PI) / 2;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(
      Math.cos(angle + 0.5) * radius * 0.6,
      Math.sin(angle + 0.5) * radius * 0.6,
      Math.cos(angle) * radius * 0.8,
      Math.sin(angle) * radius * 0.8
    );
    ctx.stroke();
  }
}

function drawCoilSymbol(ctx, halfW, halfH, scale) {
  const w = halfW * 0.8;
  const h = halfH * 0.6;

  // Coil box
  ctx.strokeRect(-w, -h, w * 2, h * 2);

  // Horizontal lines (fins)
  const numLines = 4;
  for (let i = 1; i < numLines; i++) {
    const y = -h + (h * 2 * i) / numLines;
    ctx.beginPath();
    ctx.moveTo(-w + 2, y);
    ctx.lineTo(w - 2, y);
    ctx.stroke();
  }

  // Vertical tubes
  ctx.setLineDash([2 / scale, 2 / scale]);
  for (let x = -w + w * 0.4; x < w; x += w * 0.4) {
    ctx.beginPath();
    ctx.moveTo(x, -h + 2);
    ctx.lineTo(x, h - 2);
    ctx.stroke();
  }
  ctx.setLineDash([]);
}

function drawHeatPumpSymbol(ctx, halfW, halfH, scale) {
  const radius = Math.min(halfW, halfH) * 0.6;

  // Circle
  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, Math.PI * 2);
  ctx.stroke();

  // HP text
  const fontSize = radius * 0.6;
  ctx.font = `bold ${fontSize}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('HP', 0, 0);

  // Bidirectional arrows
  const arrowSize = radius * 0.3;
  ctx.lineWidth = 1.5 / scale;

  // Left arrow
  ctx.beginPath();
  ctx.moveTo(-radius - arrowSize, 0);
  ctx.lineTo(-radius - arrowSize * 0.3, -arrowSize * 0.4);
  ctx.moveTo(-radius - arrowSize, 0);
  ctx.lineTo(-radius - arrowSize * 0.3, arrowSize * 0.4);
  ctx.stroke();

  // Right arrow
  ctx.beginPath();
  ctx.moveTo(radius + arrowSize, 0);
  ctx.lineTo(radius + arrowSize * 0.3, -arrowSize * 0.4);
  ctx.moveTo(radius + arrowSize, 0);
  ctx.lineTo(radius + arrowSize * 0.3, arrowSize * 0.4);
  ctx.stroke();
}

function drawMiniSplitHeadSymbol(ctx, halfW, halfH, scale) {
  // Louvers
  ctx.strokeRect(-halfW * 0.8, -halfH * 0.6, halfW * 1.6, halfH * 1.2);

  // Airflow lines
  const numLines = 4;
  for (let i = 0; i < numLines; i++) {
    const x = -halfW * 0.6 + (halfW * 1.2 * i) / (numLines - 1);
    ctx.beginPath();
    ctx.moveTo(x, halfH * 0.6);
    ctx.lineTo(x, halfH * 1.2);
    ctx.stroke();
  }

  // Top vent line
  ctx.beginPath();
  ctx.moveTo(-halfW * 0.6, -halfH * 0.3);
  ctx.lineTo(halfW * 0.6, -halfH * 0.3);
  ctx.stroke();
}

function drawMiniSplitOutdoorSymbol(ctx, halfW, halfH, scale) {
  const radius = Math.min(halfW, halfH) * 0.5;

  // Fan circle
  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, Math.PI * 2);
  ctx.stroke();

  // Center
  ctx.beginPath();
  ctx.arc(0, 0, radius * 0.15, 0, Math.PI * 2);
  ctx.fill();

  // Cross pattern
  ctx.beginPath();
  ctx.moveTo(0, -radius * 0.8);
  ctx.lineTo(0, radius * 0.8);
  ctx.moveTo(-radius * 0.8, 0);
  ctx.lineTo(radius * 0.8, 0);
  ctx.stroke();
}

function drawThermostatSymbol(ctx, halfW, halfH, scale) {
  const radius = Math.min(halfW, halfH) * 0.8;

  // Circle
  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, Math.PI * 2);
  ctx.stroke();

  // Temperature text
  const fontSize = radius * 0.5;
  ctx.font = `${fontSize}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('72°', 0, -radius * 0.2);

  // Button/display area
  ctx.fillStyle = ctx.strokeStyle + '40';
  ctx.fillRect(-radius * 0.5, radius * 0.1, radius, radius * 0.4);
}

/**
 * Draw equipment label
 */
function drawEquipmentLabel(ctx, equipment, equipType, width, height, scale) {
  const sizeObj = equipType.sizes?.find((s) => s.value === equipment.size);
  const label = sizeObj ? sizeObj.label : equipType.label;

  const fontSize = Math.min(10, Math.max(6, width / 4)) / scale;
  ctx.font = `bold ${fontSize}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';

  const textWidth = ctx.measureText(label).width + 4 / scale;
  const halfH = height / 2;

  // Background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
  ctx.fillRect(-textWidth / 2, halfH + 2 / scale, textWidth, fontSize + 4 / scale);

  // Text
  ctx.fillStyle = '#fff';
  ctx.fillText(label, 0, halfH + 4 / scale);
}

/**
 * Draw selection handles for equipment
 */
function drawEquipmentHandles(ctx, width, height, scale) {
  const halfW = width / 2;
  const halfH = height / 2;
  const handleSize = 5 / scale;

  ctx.fillStyle = '#00c8ff';
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 1 / scale;

  // Corner handles
  const corners = [
    [-halfW, -halfH],
    [halfW, -halfH],
    [halfW, halfH],
    [-halfW, halfH],
  ];

  corners.forEach(([x, y]) => {
    ctx.beginPath();
    ctx.rect(x - handleSize / 2, y - handleSize / 2, handleSize, handleSize);
    ctx.fill();
    ctx.stroke();
  });

  // Rotation handle
  ctx.beginPath();
  ctx.arc(0, -halfH - 12 / scale, handleSize / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Line to rotation handle
  ctx.beginPath();
  ctx.moveTo(0, -halfH);
  ctx.lineTo(0, -halfH - 12 / scale + handleSize / 2);
  ctx.stroke();
}

/**
 * Draw all equipment
 */
export function drawAllEquipment(ctx, equipment, scale, selectedEquipmentId = null) {
  if (!equipment || equipment.length === 0) return;

  // Draw non-selected equipment first
  equipment.forEach((equip) => {
    if (equip.id !== selectedEquipmentId) {
      drawEquipment(ctx, equip, scale, false, false);
    }
  });

  // Draw selected equipment on top
  const selectedEquip = equipment.find((e) => e.id === selectedEquipmentId);
  if (selectedEquip) {
    drawEquipment(ctx, selectedEquip, scale, true, false);
  }
}

/**
 * Draw equipment preview while placing
 */
export function drawEquipmentPreview(ctx, x, y, equipmentType, rotation, scale) {
  const previewEquipment = {
    x,
    y,
    type: equipmentType,
    rotation: rotation || 0,
  };

  drawEquipment(ctx, previewEquipment, scale, false, true);
}

/**
 * Hit test for equipment selection
 */
export function hitTestEquipment(worldX, worldY, equipment, scale) {
  const equipType = EQUIPMENT_TYPES[equipment.type] || EQUIPMENT_TYPES['furnace-gas'];
  const width = equipType.width;
  const height = equipType.height;

  // Transform click point to equipment's local coordinates
  const dx = worldX - equipment.x;
  const dy = worldY - equipment.y;
  const rotation = (equipment.rotation || 0) * Math.PI / 180;

  // Rotate point back to align with equipment axes
  const localX = dx * Math.cos(-rotation) - dy * Math.sin(-rotation);
  const localY = dx * Math.sin(-rotation) + dy * Math.cos(-rotation);

  // Check if point is within equipment bounds (with padding)
  const padding = Math.max(5, 5 / scale);
  const halfW = width / 2 + padding;
  const halfH = height / 2 + padding;

  return Math.abs(localX) <= halfW && Math.abs(localY) <= halfH;
}

export default {
  drawEquipment,
  drawAllEquipment,
  drawEquipmentPreview,
  hitTestEquipment,
};
